import React from 'react';
import { cn } from '../../../lib/cn';
import { useFieldControl } from './Field';

/**
 * Input, the single-line text control.
 *
 * Source selectors: `system.css` `.dsk-input` + `--error`, `--block`.
 *
 * Doctrine enforced:
 * - Error is BORDER plus the `ErrorText` line, never a filled red surface. The
 *   upstream rule is the compound `.dsk-input.dsk-input--error` so it outranks
 *   `:hover` and `:focus-visible`; we emit both classes for that reason.
 * - `error` also sets `aria-invalid`, so the state is not colour-only.
 * - Disabled is the real attribute, and the flat grey comes from the CSS.
 * - Inside a `Field` the id, `aria-describedby` and `aria-invalid` are wired
 *   automatically through `useFieldControl`; nothing needs passing by hand.
 */
export const Input = React.forwardRef(function Input(
  { error = false, block = false, type = 'text', className, ...rest },
  ref,
) {
  const field = useFieldControl(rest);

  return (
    <input
      ref={ref}
      type={type}
      className={cn(
        'dsk-input',
        error && 'dsk-input--error',
        block && 'dsk-input--block',
        className,
      )}
      {...rest}
      id={field.id}
      aria-describedby={field['aria-describedby']}
      aria-invalid={error ? true : field['aria-invalid']}
    />
  );
});
Input.displayName = 'Input';

export default Input;
